import { Component, DestroyRef, inject, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faBoxOpen, faCheck, faCubes, faRulerCombined, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { InventoryPackageTypeDTO } from '../../../../modules/inventory/models/inventory-package-type';
import { InventoryService } from '../../../../modules/pick-plan/services/inventory.service';

export interface InventoryPackageTypePopupData {
  packageType?: InventoryPackageTypeDTO;
}

@Component({
  selector: 'inventory-package-type-edit',
  templateUrl: './inventory-package-type-edit.component.html',
  styleUrls: ['./inventory-package-type-edit.component.scss']
})
export class InventoryPackageTypeEditComponent {
  private destroyRef = inject(DestroyRef);

  // Closes with true when the package type was saved or deleted.
  static open(dialog: MatDialog, data: InventoryPackageTypePopupData): MatDialogRef<InventoryPackageTypeEditComponent, boolean> {
    return dialog.open(InventoryPackageTypeEditComponent, { data, width: '760px', maxWidth: '95vw' });
  }

  public faBoxOpen = faBoxOpen;
  public faCheck = faCheck;
  public faCubes = faCubes;
  public faRulerCombined = faRulerCombined;
  public faTrash = faTrash;
  public faXmark = faXmark;

  public packageType: InventoryPackageTypeDTO;
  public isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InventoryPackageTypePopupData,
    private dialogRef: MatDialogRef<InventoryPackageTypeEditComponent, boolean>,
    private dialog: MatDialog,
    private inventoryService: InventoryService,
    private errorService: ErrorService
  ) {
    // Copy so Cancel leaves the grid row untouched.
    const source = data?.packageType ?? {};
    this.packageType = source.id ? { ...source } : { isActive: true, ...source };
  }

  submit() {
    const request: Observable<unknown> = this.packageType.id
      ? this.inventoryService.putInventoryPackageType(this.packageType)
      : this.inventoryService.postInventoryPackageType(this.packageType);
    this.run(request);
  }

  deletePackageType() {
    this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '10%',
      data: {
        confirmLabel: 'Yes',
        title: 'Delete Package Type?',
        confirmMessage: `Are you sure you want to delete ${this.packageType.name || 'this package type'}?`,
        cancelLabel: 'No'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed === true)
        this.run(this.inventoryService.deleteInventoryPackageType(this.packageType.id));
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  private run(request: Observable<unknown>) {
    this.isLoading = true;
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.dialogRef.close(true),
      error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }
}
